import { t, tc } from "./i18n"

/**
 * The Stock tab's "Change stock" editor, as plain functions: what the numbers typed per size mean
 * for each kind of change, the summary shown before saving, and the warnings. The backend
 * (lib/hq/stock/changes.ts in the Medusa app) applies the same rules again under Medusa's lock.
 *
 *   receive — units that came in, per size (only sizes with a number count)
 *   adjust  — a correction per size, + or −, with a reason
 *   count   — what is on the shelf per size; sizes left as they are change nothing
 */
export type StockKind = "receive" | "adjust" | "count"

// The labels and hints are getters: they are read when drawn, in the language chosen then, never at import.
export const KINDS: Array<{ key: StockKind; label: string; hint: string }> = [
  { key: "receive", get label() { return tc("kind", "Received") }, get hint() { return t("A parcel came in: how many of each size.") } },
  { key: "adjust", get label() { return tc("kind", "Fix") }, get hint() { return t("Add or take off units, and say why.") } },
  { key: "count", get label() { return tc("kind", "Count") }, get hint() { return t("What's on the shelf now, size by size.") } },
]

/** Same keys as the backend's ADJUST_REASONS (only the labels are translated). */
export const REASONS: Array<{ key: string; label: string }> = [
  { key: "sold_elsewhere", get label() { return tc("reason", "Sold outside the shop") } },
  { key: "returned", get label() { return tc("reason", "Returned by a customer") } },
  { key: "damaged", get label() { return tc("reason", "Damaged") } },
  { key: "lost", get label() { return tc("reason", "Lost or missing") } },
  { key: "found", get label() { return tc("reason", "Found") } },
  { key: "own_use", get label() { return tc("reason", "Given away / own use") } },
  { key: "other", get label() { return tc("reason", "Other") } },
]

/** A stock change's reason in words, including those HQ makes itself (an exchange sent from a return). */
export const reasonLabel = (key: string) => REASONS.find((r) => r.key === key)?.label ?? (key === "exchange" ? tc("reason", "Sent in an exchange") : key)

export type SizeRow = { variant_id: string | null; title: string; stocked: number | null; reserved: number | null; available: number | null }

export type ChangeLine = { variant_id: string; option: string; quantity: number; delta: number; before: number; after: number; reserved: number }

export const sizeLabel = (title: string) => (!title || /^default/i.test(title) ? "One size" : title)

/** The sizes whose stock HQ can change: tracked, with a variant id. */
export const editableSizes = (sizes: SizeRow[]) => sizes.filter((size): size is SizeRow & { variant_id: string; stocked: number } => !!size.variant_id && size.stocked != null)

/** What the typed numbers change. `draft` is variant id → the number typed for that kind. */
export function changeLines(kind: StockKind, sizes: SizeRow[], draft: Record<string, number | undefined>): ChangeLine[] {
  const lines: ChangeLine[] = []
  for (const size of editableSizes(sizes)) {
    const typed = draft[size.variant_id]
    if (typed == null || !Number.isFinite(typed)) continue
    const before = size.stocked
    let delta: number
    if (kind === "count") {
      delta = typed - before
    } else {
      delta = typed
      if (kind === "receive" && typed < 0) continue
    }
    if (delta === 0) continue
    lines.push({ variant_id: size.variant_id, option: sizeLabel(size.title), quantity: typed, delta, before, after: before + delta, reserved: size.reserved ?? 0 })
  }
  return lines
}

const signed = (n: number) => (n > 0 ? `+${n}` : `−${-n}`)

/** One line for the confirmation: "S +8 · M +8 (16 in)" / "S 4 → 3". */
export function describe(kind: StockKind, lines: ChangeLine[]): string {
  if (kind === "count") return lines.map((line) => `${line.option} ${line.before} → ${line.after}`).join(" · ")
  const parts = lines.map((line) => `${line.option} ${signed(line.delta)}`).join(" · ")
  const total = lines.reduce((sum, line) => sum + line.delta, 0)
  return kind === "receive" ? `${parts} ${t("({total} in)", { total })}` : parts
}

/** Blocking problems first (nothing is saved while there are any), then warnings to confirm past. */
export function check(kind: StockKind, lines: ChangeLine[], reason: string | null): { blocking: string[]; warnings: string[] } {
  const blocking: string[] = []
  const warnings: string[] = []
  if (!lines.length) blocking.push(kind === "count" ? t("Every count matches the stock already.") : t("Enter at least one size."))
  if (kind === "adjust" && !reason) blocking.push(t("Choose why."))
  for (const line of lines) {
    if (!Number.isInteger(line.quantity)) blocking.push(t("{size}: whole numbers only.", { size: line.option }))
    if (line.after < 0) blocking.push(t("{size}: only {n} in stock.", { size: line.option, n: line.before }))
    else if (line.after < line.reserved) {
      warnings.push(t("{size}: {reserved} reserved for orders not handed over yet — after this only {after} left for them.", { size: line.option, reserved: line.reserved, after: line.after }))
    }
  }
  return { blocking, warnings }
}

/** One per press of Save, so a retried request changes nothing twice. */
export function newRequestId(): string {
  const random = typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  return `stk-${random}`.slice(0, 64)
}
